import type { ZuoraPaymentMethod, ZuoraSignature } from '@kimuno/api-sdk/lib/services/billing';
import { Alert, Box, Button, CircularProgress, Collapse } from '@mui/material';
import config from 'config';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CreditCardSVG from '../../../assets/creditCard.svg';
import PaypalSVG from '../../../assets/paypal.svg';
import SepaSVG from '../../../assets/sepa.svg';
import { borderRadius, customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import { getSubscriptionUpgradePreview, selectUser } from '../../../reducers/user.selectors';
import services from '../../../services';
import type {
	ZuoraPaymentPageClientSideErrorHandler,
	ZuoraPaymentPageResponse,
	ZuoraPaymentPageResponseHandler,
} from '../../../types/ZuoraPayments';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import {
	isEveryPayPalPaymentObjectDefined,
	isZuoraPaymentObjectDefined,
} from '../../../utils/payment';
import { SleepUntilAbortedError, sleepUntil } from '../../../utils/sleep';
import PaypalForm from '../../PaypalForm';

const PAYMENT_METHOD_CREDIT_CARD: ZuoraPaymentMethod = 'creditCard';
const PAYMENT_METHOD_SEPA: ZuoraPaymentMethod = 'sepa';
const PAYMENT_METHOD_PAYPAL: ZuoraPaymentMethod = 'braintree';

type PaymentMethod = {
	id: ZuoraPaymentMethod;
	icon: JSX.Element;
	label: string;
	dataTestId: string;
};

const PAYMENT_METHODS = [
	{
		id: PAYMENT_METHOD_SEPA,
		icon: <SepaSVG />,
		label: 'SEPA',
		dataTestId: 'sepa-payment-method',
	},
	{
		id: PAYMENT_METHOD_PAYPAL,
		icon: <PaypalSVG />,
		label: 'PayPal',
		dataTestId: 'paypal-payment-method',
	},
	{
		id: PAYMENT_METHOD_CREDIT_CARD,
		icon: <CreditCardSVG />,
		label: 'Credit Card',
		dataTestId: 'credit-card-payment-method',
	},
] as PaymentMethod[];
interface SinglePagePaymentMethodsProps {
	onSelect: (paymentMethod: ZuoraPaymentMethod) => void;
	onResponse: ZuoraPaymentPageResponseHandler;
	onClientSideErrorHandler?: ZuoraPaymentPageClientSideErrorHandler;
	selectedMethod?: ZuoraPaymentMethod;
	showPaymentForm: boolean;
	onChange?: (clicked: boolean) => void; // This prop is passed in by <Controller />
	zuoraHostedPagesSource: string;
	paymentProviderLoadTimeoutMs?: number;
}

interface TabPanelProps {
	children?: React.ReactNode;
	value?: ZuoraPaymentMethod;
	index: ZuoraPaymentMethod;
}

const CustomTabPanel = (props: TabPanelProps) => {
	const { children, value, index, ...other } = props;

	return (
		<Collapse in={value === index} {...other}>
			{value === index && <Box sx={{ paddingTop: spaces.lg }}>{children}</Box>}
		</Collapse>
	);
};

const PaymentMethods: React.FC<SinglePagePaymentMethodsProps> = (props) => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [loadingError, setLoadingError] = useState<ZuoraPaymentMethod | undefined>();
	const [hasCreditCardErrors, setHasCreditCardErrors] = useState<boolean>(false);
	const subscriptionUpgradePreview = useSelector(getSubscriptionUpgradePreview);
	const user = useSelector(selectUser);
	const [paypalToken, setPaypalToken] = useState('');
	const { paymentProviderLoadTimeoutMs = 12000, selectedMethod } = props;

	const renderPaymentPage = (
		signature: ZuoraSignature,
		paymentMethod: ZuoraPaymentMethod,
		onResponseHandler: ZuoraPaymentPageResponseHandler,
		onClientSideErrorHandler?: ZuoraPaymentPageClientSideErrorHandler
	) => {
		const params = {
			locale: 'de_DE',
			style: 'inline',
			submitEnabled: 'true',
			url: config.zuoraHostedPagesUri,
			id: signature.pageId,
			key: signature.key,
			signature: signature.signature,
			token: signature.token,
			tenantId: signature.tenantId,
		};

		const onClientSideErrorHandlerProxy = (key: string, code: string, message: string) => {
			// Send the error to the UI
			window.Z.sendErrorMessageToHpm(key, message);
			// Invoke our custom client-side-error-handler
			if (onClientSideErrorHandler) onClientSideErrorHandler(key, code, message);
			setHasCreditCardErrors(true);
		};

		window.Z.renderWithErrorHandler(
			params,
			{},
			(response: ZuoraPaymentPageResponse) => onResponseHandler(paymentMethod, response),
			onClientSideErrorHandlerProxy
		);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!selectedMethod) {
			return;
		}

		// The Zuora readiness poll below runs on a real interval. Without
		// cancelling it, it outlives both an unmount and a switch to another
		// method — so it keeps reaching for `window.Z`, and a stale poll that
		// eventually resolves would render the *previous* method's payment form.
		const controller = new AbortController();
		setupPaymentMethod(selectedMethod, controller.signal);

		return () => controller.abort();
	}, [selectedMethod]);

	const handleSelect = (paymentMethod: ZuoraPaymentMethod) => {
		props.onSelect(paymentMethod);
		props.onChange?.(true);
	};

	const setupPaymentMethod = async (paymentMethod: ZuoraPaymentMethod, signal?: AbortSignal) => {
		setLoadingError(undefined);
		setPaypalToken('');

		if (paymentMethod === PAYMENT_METHOD_PAYPAL) {
			if (!isEveryPayPalPaymentObjectDefined()) {
				const error = `Not every PayPal payment object is defined.
          !!window.braintree.client: ${!!window.braintree?.client},
          !!window.braintree.paypalCheckout: ${!!window.braintree?.paypalCheckout},
          !!window.paypal.Button: ${!!window.paypal?.Button},`
					.replace(/(\r\n|\n|\r)/gm, '')
					.replace(/\s{2,}/g, ' ');

				notifyBugsnagHandledError(new Error(error));

				setLoadingError(paymentMethod);
				return;
			}

			try {
				const data = await services.billing.getBraintreeSignature();

				// Same reasoning as the Zuora branch below: the request cannot be
				// cancelled, so a run superseded while it was in flight must not touch
				// state that now belongs to whichever method is selected.
				if (signal?.aborted) {
					return;
				}

				setPaypalToken(data.token);
				setIsLoading(false);
			} catch {
				if (signal?.aborted) {
					return;
				}

				setLoadingError(paymentMethod);
			}
			return;
		}

		setIsLoading(true);

		try {
			await sleepUntil(isZuoraPaymentObjectDefined, paymentProviderLoadTimeoutMs, signal);
		} catch (error) {
			// Cancelled rather than timed out: the component is unmounting or has
			// already moved to another method, so there is no state left to set and
			// nothing worth reporting. Returning here is also what keeps the
			// `window.Z` read below from running against a torn-down environment.
			if (error instanceof SleepUntilAbortedError) {
				return;
			}

			if (!isZuoraPaymentObjectDefined()) {
				const message = `Zuora payment object is not defined. !!window.Z: ${!!window.Z}`;
				notifyBugsnagHandledError(new Error(message));
			}

			setLoadingError(paymentMethod);
			return;
		}

		try {
			const signature = await services.billing.getSignature(
				paymentMethod,
				props.zuoraHostedPagesSource
			);

			// The signature request is not cancellable, so it can land after the
			// user has moved on. Everything below belongs to `paymentMethod`: the
			// render would mount that method's Zuora form over the one now
			// selected, and setIsLoading(false) would report the *current* run as
			// ready. A superseded run has to stop here.
			if (signal?.aborted) {
				return;
			}

			renderPaymentPage(signature, paymentMethod, props.onResponse, props.onClientSideErrorHandler);
			setIsLoading(false);
		} catch {
			if (signal?.aborted) {
				return;
			}

			setLoadingError(paymentMethod);
		}
	};

	const getFilteredPaymentMethods = () => {
		let paymentMethods = PAYMENT_METHODS;
		if ((user?.isBibliothekUser || user?.isCourseUser) && subscriptionUpgradePreview?.paymentMethod) {
			const { paymentMethod } = subscriptionUpgradePreview;
			let method: string | null = paymentMethod;
			switch (subscriptionUpgradePreview.paymentMethod) {
				case 'bankTransfer':
				case 'sepa':
					method = PAYMENT_METHOD_SEPA;
					break;
				case 'creditCard':
					method = PAYMENT_METHOD_CREDIT_CARD;
					break;
				case 'paypal':
					method = PAYMENT_METHOD_PAYPAL;
					break;
				default:
					method = null;
					break;
			}

			if (method) paymentMethods = PAYMENT_METHODS.filter((m) => m.id === method);
		}

		return paymentMethods.map(({ id, icon, dataTestId }) => (
			<Button
				data-testid={dataTestId}
				key={id}
				size="large"
				sx={{
					borderRadius: borderRadius.xs,
					borderColor: selectedMethod === id ? palette.primary.main : palette.elevation.outlined,
					'& svg': { width: 'auto', height: 'auto' },
				}}
				variant="outlined"
				onClick={() => handleSelect(id)}
			>
				{icon}
			</Button>
		));
	};

	const memoizedPaypalForm = useMemo(() => {
		return <PaypalForm token={paypalToken} licenceId={1} onResponse={props.onResponse} />;
	}, [paypalToken, props.onResponse]);

	const renderPaymentContent = (method: ZuoraPaymentMethod) => {
		switch (method) {
			case PAYMENT_METHOD_SEPA:
				return (
					<Box
						id="zuora_payment"
						data-testid="sepa-payment-page"
						sx={{
							minHeight: customSizes.paymentMethods.sepaForm.height,
							'& iframe': {
								with: '100%',
								minHeight: 'inherit',
								height: 'auto',
							},
						}}
					/>
				);
			case PAYMENT_METHOD_CREDIT_CARD:
				return (
					<Box
						id="zuora_payment"
						data-testid="credit-card-payment-page"
						sx={{
							minHeight: {
								xs: customSizes.paymentMethods.creditCardForm.height.mobile,
								lg: customSizes.paymentMethods.creditCardForm.height.desktop,
							},
							'& iframe': {
								with: '100%',
								minHeight: 'inherit',
								...(!hasCreditCardErrors ? { height: 'auto' } : {}),
							},
						}}
					/>
				);
			case PAYMENT_METHOD_PAYPAL:
				return memoizedPaypalForm;
			default:
				return null;
		}
	};

	return (
		<Box sx={{ width: '100%' }}>
			<Box sx={{ display: 'flex', gap: spaces.xxs }}>{getFilteredPaymentMethods()}</Box>
			{loadingError ? (
				<Alert severity="warning" sx={{ marginTop: spaces.lg }}>
					{t('Error while loading payment form, please refresh.')}
				</Alert>
			) : (
				<>
					{isLoading && (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								marginTop: spaces.lg,
							}}
						>
							<CircularProgress
								color="primary"
								sx={{
									'& .MuiCircularProgress-svg': {
										height: 'auto',
										width: 'auto',
									},
								}}
							/>
						</Box>
					)}
					{PAYMENT_METHODS.map(({ id }) => (
						<CustomTabPanel key={id} value={selectedMethod} index={id}>
							{renderPaymentContent(id)}
						</CustomTabPanel>
					))}
				</>
			)}
		</Box>
	);
};

export default PaymentMethods;
